import type { ReactNode } from 'react'
import { Reveal } from '@/components/motion/Reveal'
import { SplitText } from '@/components/motion/SplitText'
import { useSite } from '@/app/site-context'
import { reviewNoteFor } from '@/i18n/review-notes'
import { seatLine, siteConfig } from '@/lib/site-config'

interface LegalPageProps {
  onNavigate: (href: string) => void
}

export function LegalPage({ onNavigate }: LegalPageProps) {
  const { dictionary, pathFor, locale } = useSite()
  const { legal, contact } = dictionary
  const reviewNote = reviewNoteFor(locale)
  const home = pathFor('home')

  const rows: { label: string; value: ReactNode }[] = [
    { label: legal.nameLabel, value: siteConfig.legalName },
    { label: legal.shortNameLabel, value: siteConfig.shortLegalName },
    { label: legal.seatLabel, value: seatLine(legal.country) },
    { label: legal.registerLabel, value: legal.registerValue },
    { label: legal.mboLabel, value: siteConfig.register.mbo },
    {
      label: legal.registeredOnLabel,
      value: (
        <time dateTime={siteConfig.register.registeredOn}>{siteConfig.register.registeredOn}</time>
      ),
    },
    { label: legal.activityLabel, value: legal.activityValue },
    {
      label: contact.emailLabel,
      value: <a href={`mailto:${siteConfig.email}`}>{siteConfig.email}</a>,
    },
    {
      label: contact.phoneLabel,
      value: <a href={`tel:${siteConfig.phone.replace(/\s/g, '')}`}>{siteConfig.phone}</a>,
    },
  ]

  return (
    <section className="prose section" aria-labelledby="legal-title">
      <h1 className="prose__title" id="legal-title">
        <SplitText text={legal.title} stagger={40} />
      </h1>
      {reviewNote === undefined ? null : (
        <p className="prose__meta policy__languages">{reviewNote.legal}</p>
      )}
      <Reveal variant="rise">
        <p className="prose__lede">{legal.intro}</p>
      </Reveal>

      <Reveal variant="rise" delay={60}>
        <dl className="legal-list">
          {rows.map((row) => (
            <div className="legal-list__row" key={row.label}>
              <dt className="legal-list__label">{row.label}</dt>
              <dd className="legal-list__value">{row.value}</dd>
            </div>
          ))}
        </dl>
      </Reveal>

      <a
        className="button button--ghost prose__back"
        href={home}
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
          event.preventDefault()
          onNavigate(home)
        }}
      >
        {legal.backHome}
      </a>
    </section>
  )
}
